import { useState, useEffect } from 'react'
import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useMutation, useQueryClient } from 'react-query'

// Libs
import { setLiveRoomOn } from '@/api/setLiveRoomOn'
import { setLiveType } from '@/redux/reducers/room'

// Components
import Button from '@/components/common/Button'
import Icon from '@/assets/icons'

// Constants
import { QUERY_KEY } from '@/constants/queryKey'
import { ROOM_LIVE_TYPE } from '@/constants/liveRoom'
import { ROLE } from '@/constants/role'

// Types

// Styles
import styles from './style.module.scss'
import ActionIcon from '@/components/common/ActionIcon'
import { setInterval } from 'timers'

const cx = classnames.bind(styles)

interface PrepareActionProps {
  isMicEnabled: boolean
  isCameraEnabled: boolean
  isDisabledCameraSwitch: boolean
  isMultiCamera: boolean
  handleMicStatus: () => void
  handleCameraStatus: () => void
  handleCameraSwitch: () => void
  role: any
}

const START_COUNTDOWN = 3
const BUFFER_COUNT = 1 // 給倒數完直播開始的顯示時間

const PrepareAction = (props: PrepareActionProps) => {
  const { isMicEnabled, isCameraEnabled, isDisabledCameraSwitch, isMultiCamera, handleMicStatus, handleCameraStatus, handleCameraSwitch, role } =
    props

  const { t } = useTranslation()

  const isIt = role === ROLE.IT
  const [isStartCounting, setIsStartCounting] = useState(false)
  const [countdown, setCountdown] = useState(START_COUNTDOWN)

  const router = useRouter()
  const { query } = router
  const { roomId = '' } = query

  const setLiveRoomOnMutation = useMutation(setLiveRoomOn)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!isStartCounting) return
    const timer = setInterval(() => {
      if (countdown > 0) setCountdown((prev) => prev - 1)
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [isStartCounting])

  const handleLiveRoomStart = async () => {
    setIsStartCounting(true)
    setLiveType({ liveType: ROOM_LIVE_TYPE.CURRENT })

    setTimeout(() => {
      setLiveRoomOnMutation.mutateAsync({ roomId }).then(() => {
        queryClient.invalidateQueries(QUERY_KEY.FETCH_ROOM_DATA)
      })

      setIsStartCounting(false)
    }, (START_COUNTDOWN + BUFFER_COUNT) * 1000)
  }

  return (
    <div className={cx('prepareAction', { isCounting: isStartCounting })}>
      {!isStartCounting && (
        <>
          {!isIt && (
            <div className={cx('prepareAction-wrapper')}>
              <div className={cx('prepareAction-icon')} onClick={handleMicStatus}>
                <ActionIcon icon={isMicEnabled ? Icon.Mic : Icon.MicOff} />
              </div>
              <div className={cx('prepareAction-icon')} onClick={handleCameraStatus}>
                <ActionIcon icon={isCameraEnabled ? Icon.Camera : Icon.CameraOff} />
              </div>
              {isMultiCamera && (
                <div className={cx('prepareAction-icon')} onClick={handleCameraSwitch}>
                  <ActionIcon icon={Icon.Switch} isDisabled={isDisabledCameraSwitch} />
                </div>
              )}
            </div>
          )}

          <Button onClick={handleLiveRoomStart}>{t('LiveRoom_Start')}</Button>
        </>
      )}

      {isStartCounting && (
        <div className={cx('prepareAction-countdown')}>
          {countdown > 0 && <p className={cx('prepareAction-countdown-number')}>{countdown}</p>}
          {countdown <= 0 && <p className={cx('prepareAction-countdown-text')}>{t('LiveRoom_Started')}</p>}
        </div>
      )}
    </div>
  )
}

export default PrepareAction
